import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { TESTIDS } from "@/lib/testIds";
import { adminHeaders, setAdminToken } from "@/lib/api";
import CorpusPanel from "@/components/admin/CorpusPanel";
import { InvitesPanel, TeamLinksPanel, AuditPanel } from "@/components/admin/AdminPanels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "impactvest_admin_token";

const TABS = [
  { key: "applications", label: "Pilot Applications" },
  { key: "corpus", label: "Knowledge Corpus" },
  { key: "invites", label: "Invitations" },
  { key: "team", label: "Team Links" },
  { key: "audit", label: "Audit Log" },
];

const STATUSES = [
  "New",
  "Reviewing",
  "Conversation Requested",
  "Approved",
  "Invited",
  "Declined",
  "Archived",
];

export default function Admin() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("applications");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    document.title = "Admin · Pilot Applications | ImpactVest";
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      setToken(stored);
      setAuthed(true);
    }
  }, []);

  const load = useCallback(
    async (t = token, status = filter) => {
      if (!t) return;
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${t}` };
        const q = status && status !== "All" ? `?status=${encodeURIComponent(status)}` : "";
        const [listRes, statsRes] = await Promise.all([
          axios.get(`${API}/admin/pilot-applications${q}`, { headers }),
          axios.get(`${API}/admin/pilot-applications/stats`, { headers }),
        ]);
        setApps(listRes.data || []);
        setStats(statsRes.data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Unable to load applications.");
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          sessionStorage.removeItem(STORAGE_KEY);
          setAuthed(false);
        }
      } finally {
        setLoading(false);
      }
    },
    [token, filter]
  );

  useEffect(() => {
    if (authed) load();
  }, [authed, filter, load]);

  const authenticate = async (e) => {
    e.preventDefault();
    try {
      await axios.get(`${API}/admin/pilot-applications/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      sessionStorage.setItem(STORAGE_KEY, token);
      setAuthed(true);
      toast.success("Admin authenticated.");
    } catch (err) {
      toast.error("Invalid admin token.");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await axios.patch(
        `${API}/admin/pilot-applications/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApps((list) => list.map((a) => (a.id === id ? res.data : a)));
      if (selected?.id === id) setSelected(res.data);
      toast.success(`Status → ${status}`);
    } catch (err) {
      toast.error("Unable to update status.");
    }
  };

  if (!authed) {
    return (
      <div>
        <Header />
        <main className="pt-28 md:pt-32 pb-24 min-h-[70vh]">
          <div className="iv-container">
            <div className="iv-eyebrow mb-8">Platform Administrator</div>
            <h1 className="font-serif-display text-[42px] md:text-[56px] leading-[0.98] tracking-tight text-iv-fg">
              Pilot Application Queue
            </h1>
            <p className="mt-6 text-[15px] text-iv-fg-2 max-w-lg">
              Provide the administrator token to access the pilot application queue.
            </p>
            <form
              data-testid={TESTIDS.admin.tokenForm}
              onSubmit={authenticate}
              className="mt-10 max-w-md iv-panel p-8"
            >
              <label className="iv-field">
                <span>Admin Token</span>
                <input
                  data-testid={TESTIDS.admin.tokenInput}
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Enter administrator token"
                  autoFocus
                />
              </label>
              <button
                data-testid={TESTIDS.admin.tokenSubmit}
                type="submit"
                className="iv-btn iv-btn--primary mt-8"
              >
                Authenticate
              </button>
              <p className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-iv-fg-3 mt-6">
                Token stored in this browser session only.
              </p>
            </form>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const exportApplications = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ format });
      if (filter && filter !== "All") params.set("status", filter);
      const res = await axios.get(`${API}/admin/pilot-applications/export?${params.toString()}`, {
        headers: adminHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pilot-applications.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${format.toUpperCase()}.`);
    } catch {
      toast.error("Export failed.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <Header />
      <main className="pt-28 md:pt-32 pb-24">
        <div className="iv-container">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="iv-eyebrow mb-4">Platform Administration</div>
              <h1 className="font-serif-display text-[36px] md:text-[46px] leading-tight tracking-tight text-iv-fg">
                ImpactVest Console
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/" className="iv-btn iv-btn--ghost">Return to site</Link>
              <button
                onClick={() => { setAdminToken(null); sessionStorage.removeItem(STORAGE_KEY); setAuthed(false); setToken(""); }}
                className="iv-btn"
              >
                Sign Out
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-8 flex items-center gap-1 border-b border-iv-line overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-4 py-3 text-[13px] font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                  tab === t.key ? "border-iv-green text-iv-fg" : "border-transparent text-iv-fg-3 hover:text-iv-fg"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="mt-8">
            {tab === "corpus" && <CorpusPanel />}
            {tab === "invites" && <InvitesPanel />}
            {tab === "team" && <TeamLinksPanel />}
            {tab === "audit" && <AuditPanel />}
            {tab === "applications" && (
              <>
          {stats && (
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 border border-iv-line">
              <StatCard label="Total" value={stats.total} tone="gold" />
              {STATUSES.map((s) => (
                <StatCard key={s} label={s} value={stats.by_status?.[s] ?? 0} />
              ))}
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-2">
            {["All", ...STATUSES].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`iv-chip ${filter === s ? "iv-chip--gold" : ""}`}
              >
                {s}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <button onClick={() => exportApplications("csv")} disabled={exporting} className="iv-btn iv-btn--ghost" data-testid="admin-export-csv">Export CSV</button>
              <button onClick={() => exportApplications("json")} disabled={exporting} className="iv-btn iv-btn--ghost" data-testid="admin-export-json">Export JSON</button>
              <button onClick={() => load(token, filter)} className="iv-btn iv-btn--ghost">
                {loading ? "Loading…" : "Refresh"}
              </button>
            </div>
          </div>

          <div className="mt-6 border-t border-iv-line" data-testid={TESTIDS.admin.queue}>
            {apps.length === 0 ? (
              <div className="py-16 text-center text-iv-fg-3 font-mono text-[12px] tracking-[0.14em] uppercase">
                No applications in this view.
              </div>
            ) : (
              apps.map((a) => (
                <div
                  key={a.id}
                  data-testid={TESTIDS.admin.row}
                  className="grid grid-cols-12 gap-4 items-baseline px-4 py-5 border-b border-iv-line hover:bg-white/[0.015]"
                >
                  <div className="col-span-12 md:col-span-4">
                    <div className="font-serif-display text-[20px] text-iv-fg leading-snug">{a.organization}</div>
                    <div className="text-[13px] text-iv-fg-2">{a.full_name} · {a.role}</div>
                    <div className="font-mono text-[10.5px] text-iv-fg-3 mt-1">{a.professional_email}</div>
                  </div>
                  <div className="col-span-6 md:col-span-2 text-[13px] text-iv-fg-2">{a.organization_type}</div>
                  <div className="col-span-6 md:col-span-2 text-[13px] text-iv-fg-2">{a.primary_geography}</div>
                  <div className="col-span-6 md:col-span-2 font-mono text-[11px] text-iv-fg-3">
                    {new Date(a.submitted_at).toLocaleDateString()}
                  </div>
                  <div className="col-span-6 md:col-span-2 flex md:justify-end items-center gap-2">
                    <select
                      data-testid={TESTIDS.admin.statusSelect}
                      value={a.status}
                      onChange={(e) => updateStatus(a.id, e.target.value)}
                      className="bg-iv-bg-2 border border-iv-line-strong text-[12px] px-2 py-1 font-mono uppercase tracking-widest text-iv-fg"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-12">
                    <button
                      onClick={() => setSelected(selected?.id === a.id ? null : a)}
                      className="text-[12px] text-iv-gold font-mono uppercase tracking-widest hover:opacity-80"
                    >
                      {selected?.id === a.id ? "Hide details" : "View details"}
                    </button>
                    {selected?.id === a.id && <ApplicationDetail app={a} />}
                  </div>
                </div>
              ))
            )}
          </div>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function StatCard({ label, value, tone }) {
  return (
    <div className={`px-5 py-4 border-r last:border-r-0 border-iv-line ${tone === "gold" ? "bg-white/[0.02]" : ""}`}>
      <div className="font-mono-tag text-iv-fg-3">{label}</div>
      <div className={`font-serif-display text-[32px] leading-none mt-1 ${tone === "gold" ? "text-iv-gold" : "text-iv-fg"}`}>
        {value}
      </div>
    </div>
  );
}

function ApplicationDetail({ app }) {
  const rows = [
    ["AUM", app.approximate_aum || "—"],
    ["Investment Type", app.typical_investment_type],
    ["Direct / Co-invest", app.direct_or_coinvestment],
    ["Expected Users", app.expected_pilot_users],
    ["Current Diligence", app.current_diligence_process],
    ["Principal Challenge", app.principal_challenge],
    ["What to Test", app.what_they_want_to_test],
    ["Consent", app.consent_to_contact ? "Yes" : "No"],
    ["Submitted", new Date(app.submitted_at).toLocaleString()],
    ["Updated", new Date(app.updated_at).toLocaleString()],
  ];
  return (
    <div className="mt-4 iv-panel p-6">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-3">
        {rows.map(([k, v]) => (
          <div key={k} className="md:col-span-6 border-b border-iv-line pb-2">
            <div className="font-mono-tag text-iv-fg-3">{k}</div>
            <div className="text-[13.5px] text-iv-fg mt-1 leading-snug">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
